import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Brain, Plus, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { LessonCard } from './LessonCard';
import { LessonForm } from './LessonForm';

const err = (e) => toast.error(e?.response?.data?.detail || 'No se pudo completar');

// Admin view of what Fidem has learned: lessons from closed cases, hand-written lessons and lawyers' evaluation notes
export const FidemMemory = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(async () => { try { setData((await api.get('/admin/fidem/memory')).data); } catch (e) { err(e); } }, [api]);
  useEffect(() => { load(); }, [load]);

  const add = async (body) => { try { await api.post('/admin/fidem/memory', body); toast.success('Fidem aprendió la lección'); setAdding(false); load(); } catch (e) { err(e); } };
  const save = async (kind, id, body) => { try { await api.put(`/admin/fidem/memory/${kind}/${encodeURIComponent(id)}`, body); toast.success('Lección actualizada'); load(); return true; } catch (e) { err(e); return false; } };
  const remove = async (kind, id) => {
    if (!window.confirm('¿Borrar esta lección? Fidem dejará de usarla.')) return;
    try { await api.delete(`/admin/fidem/memory/${kind}/${encodeURIComponent(id)}`); toast.success('Lección borrada'); load(); } catch (e) { err(e); }
  };

  return (
    <div data-testid="fidem-memory" className="flex flex-col gap-8">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold"><Brain className="h-5 w-5 text-amber-400" /> Memoria de Fidem</h2>
          <p className="text-sm text-slate-400">Lo que Fidem aplica en sus respuestas: lecciones de causas cerradas, las que escribes tú y las correcciones de los abogados.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <button data-testid="fidem-memory-refresh" onClick={load} className="rounded-lg border border-slate-700 p-2 text-slate-400 hover:text-white"><RefreshCw className="h-4 w-4" /></button>
          <button data-testid="fidem-memory-add" onClick={() => setAdding(!adding)} className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:opacity-90"><Plus className="h-4 w-4" /> Añadir lección</button>
        </div>
      </div>
      {data && <MemoryStats stats={data.stats} />}
      {adding && <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4"><LessonForm testid="fidem-memory-new" onSave={add} onCancel={() => setAdding(false)} /></div>}
      {!data ? <p className="text-sm text-slate-500">Cargando…</p> : <>
        <Section title="Lecciones" empty="Aún no hay lecciones: se crean al cerrar causas ganadas o perdidas, o puedes añadirlas a mano." testid="fidem-memory-lessons">
          {data.lessons.map((x) => <LessonCard key={x.id} item={x} kind="lesson" onSave={save} onDelete={remove} />)}
        </Section>
        <Section title="Correcciones de los abogados" empty="Nadie ha marcado aún una respuesta como no útil con comentario." testid="fidem-memory-feedback">
          {data.feedback.map((x) => <LessonCard key={x.id} item={x} kind="feedback" onSave={save} onDelete={remove} />)}
        </Section>
      </>}
    </div>
  );
};

const MemoryStats = ({ stats }) => (
  <div className="grid grid-cols-3 gap-3">
    {[['causas', 'De causas cerradas'], ['manuales', 'Escritas a mano'], ['evaluaciones', 'Correcciones']].map(([k, l]) => (
      <div key={k} data-testid={`fidem-memory-stat-${k}`} className="rounded-xl border border-slate-800 bg-slate-900/60 p-3"><p className="text-2xl font-bold">{stats[k]}</p><p className="text-xs text-slate-400">{l}</p></div>
    ))}
  </div>
);

const Section = ({ title, empty, testid, children }) => (
  <div data-testid={testid} className="flex flex-col gap-3">
    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{title}</h3>
    {React.Children.count(children) ? children : <p className="text-sm text-slate-500">{empty}</p>}
  </div>
);
